import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MonthYearFilterValue } from '../filterTypes';
import type { FilterFieldProps } from '../FilterField.types';
import { FieldLabel } from '../FilterFieldHelpers';
import { textFieldSx } from '../styles/filterColumnSx';
import '../styles/filterPanel.css';

const PLACEHOLDER = 'yyyy/mm';

// Native <input type="month"> speaks 'YYYY-MM'; the filter value is 'YYYYMM'.
const toInput = (v: string): string => (v.length === 6 ? `${v.slice(0, 4)}-${v.slice(4)}` : '');
const toValue = (input: string): string => input.replace('-', '');

// While empty, hide the browser's native segment placeholder ('__/____') so only
// our written 'yyyy/mm' overlay shows. WebKit/Blink; Firefox keeps its own hint.
const HIDE_NATIVE_EMPTY = {
  '& input::-webkit-datetime-edit': { color: 'transparent' },
} as const;

/**
 * Month/year filter for `YYYYMM`-encoded columns (e.g. `bulletin.mois`).
 * Keeps the **native** month picker (`<input type="month">`, no extra dependency)
 * and overlays a **written** `yyyy/mm` placeholder while empty (the native segment
 * hints are hidden). Serializes to `col[like]=YYYYMM` (exact month).
 */
export function MonthYearFilterInput({
  value,
  onChange,
  onClear,
  variant = 'panel',
  label,
}: FilterFieldProps) {
  const theme = useTheme();
  const isColumn = variant === 'column';
  const current = (value as MonthYearFilterValue | undefined)?.value ?? '';
  const inputValue = toInput(current);
  const hasValue = current !== '';

  const handleChange = (raw: string) => {
    if (!raw) onClear();
    else onChange({ kind: 'monthYear', value: toValue(raw) });
  };

  // Written placeholder shown over the (blanked) native field while empty.
  const overlay = !hasValue ? (
    <Box
      component="span"
      aria-hidden
      sx={{
        position: 'absolute',
        left: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: theme.palette.text.disabled,
        fontSize: theme.typography.body2.fontSize,
      }}
    >
      {PLACEHOLDER}
    </Box>
  ) : null;

  if (isColumn) {
    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          size="small"
          fullWidth
          type="month"
          value={inputValue}
          onChange={e => handleChange(e.target.value)}
          sx={{ ...textFieldSx(theme), ...(hasValue ? {} : HIDE_NATIVE_EMPTY) }}
          slotProps={{
            inputLabel: { shrink: true },
            ...(hasValue && {
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={onClear}
                      edge="end"
                      tabIndex={-1}
                      sx={{ p: 0.25 }}
                    >
                      <ClearIcon sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }),
          }}
        />
        {overlay}
      </Box>
    );
  }

  return (
    <Box className="filter-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch', height: '100%' }}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <TextField
            size="small"
            fullWidth
            type="month"
            value={inputValue}
            onChange={e => handleChange(e.target.value)}
            className="filter-input"
            sx={hasValue ? undefined : HIDE_NATIVE_EMPTY}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {overlay}
        </Box>
        {hasValue && (
          <IconButton size="small" onClick={onClear} className="filter-clear-btn">
            <ClearIcon className="filter-clear-icon" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
